import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCampusDetails } from "../../../redux/campusesSlice";
import ViewReportModal from "./ViewReportModal";
import AttendanceReport from "./AttendanceReport";
import IndividualAttendanceReport from "./IndividualAttendanceReport";
import toast from "react-hot-toast";

const Attendance = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedCampusId, setSelectedCampusId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewSectionReport, setViewSectionReport] = useState(false);
  const [viewIndividualReport, setViewIndividualReport] = useState(false);
  
  const { campusDetails, loading } = useSelector((state) => state.campuses);
  
  const selectedCampus = campusDetails.find(campus => campus.id === selectedCampusId) || { classes: [] };
  const availableClasses = selectedCampus.classes || [];
  
  const selectedClass = availableClasses.find(cls => cls.id === selectedClassId) || { sections: [] };
  const availableSections = selectedClass.sections || [];
  
  const selectedSection = availableSections.find(section => section.id === selectedSectionId) || { students: [] };
  const availableStudents = selectedSection.students || [];
  
  // Add dummy students and teachers to the available members list for selection
  const dummyStudents = [
    { id: "ST001", fullName: "Ahmed Ali", role: "Student" },
    { id: "ST002", fullName: "Fatima Khan", role: "Student" },
    { id: "ST003", fullName: "Hassan Sheikh", role: "Student" },
    { id: "ST004", fullName: "Aisha Malik", role: "Student" },
    { id: "ST005", fullName: "Omar Farooq", role: "Student" },
    { id: "ST006", fullName: "Zainab Ahmed", role: "Student" },
    { id: "ST007", fullName: "Yusuf Hassan", role: "Student" },
    { id: "ST008", fullName: "Maryam Khan", role: "Student" }
  ];

  const dummyTeachers = [
    { id: "T001", fullName: "Dr. Sarah Johnson", role: "Teacher" },
    { id: "T002", fullName: "Mr. Michael Brown", role: "Teacher" },
    { id: "T003", fullName: "Ms. Emily Davis", role: "Teacher" },
    { id: "T004", fullName: "Prof. David Wilson", role: "Teacher" }
  ];
  
  const allAvailableMembers = [...availableStudents, ...dummyStudents, ...dummyTeachers];
  
  // Filter members based on selected user type
  const filteredMembers = selectedUserType 
    ? allAvailableMembers.filter(member => member.role === selectedUserType)
    : allAvailableMembers;

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchCampusDetails());
    }
  }, [isOpen, dispatch]);
  
  useEffect(() => {
    if (selectedCampusId === "") {
      setSelectedClassId("");
      setSelectedSectionId("");
      setSelectedStudentId("");
      setSelectedUserType("");
    }
  }, [selectedCampusId]);
  
  useEffect(() => {
    if (selectedClassId === "") {
      setSelectedSectionId("");
      setSelectedStudentId("");
      setSelectedUserType("");
    }
  }, [selectedClassId]);
  
  useEffect(() => {
    if (selectedSectionId === "") {
      setSelectedStudentId("");
      setSelectedUserType("");
    }
  }, [selectedSectionId]);
  
  useEffect(() => {
    if (selectedUserType === "") {
      setSelectedStudentId("");
    }
  }, [selectedUserType]);


  const handleViewSectionReport = () => {
    if (selectedSectionId) {
      if (!startDate || !endDate) {
        toast.error("Please select date range first");
        return;
      }
      setViewSectionReport(true);
      setViewIndividualReport(false);
      setViewReportOpen(true);
    } else {
      toast.error("Please select a section first");
    }
  };

  const handleViewIndividualReport = () => {
    if (selectedStudentId) {
      if (!startDate || !endDate) {
        toast.error("Please select date range first");
        return;
      }
      setViewIndividualReport(true);
      setViewSectionReport(false);
      setViewReportOpen(true);
    } else {
      toast.error("Please select a teacher or student first");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-6 rounded-lg shadow-lg w-[60%] relative">
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="campus" className="block mb-2">
                Campus
              </label>
              <select 
                id="campus" 
                className="border p-2 rounded w-full"
                value={selectedCampusId}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                disabled={loading}
              >
                <option value="" disabled>
                  Choose a campus
                </option>
                {campusDetails.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="grade" className="block mb-2">
                Grade
              </label>
              <select 
                id="grade" 
                className="border p-2 rounded w-full"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                disabled={!selectedCampusId || loading}
              >
                <option value="" disabled>
                  Choose a grade
                </option>
                {availableClasses.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.className}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="section" className="block mb-2">
                Section
              </label>
              <select 
                id="section" 
                className="border p-2 rounded w-full"
                value={selectedSectionId}
                onChange={(e) => setSelectedSectionId(e.target.value)}
                disabled={!selectedClassId || loading}
              >
                <option value="" disabled>
                  Choose a section
                </option>
                {availableSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.sectionName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="userType" className="block mb-2">
                User Type
              </label>
              <select 
                id="userType" 
                className="border p-2 rounded w-full"
                value={selectedUserType}
                onChange={(e) => setSelectedUserType(e.target.value)}
                disabled={!selectedSectionId || loading}
              >
                <option value="" disabled>
                  Choose user type
                </option>
                <option value="Student">Student</option>
                <option value="Teacher">Teacher</option>
                <option value="">All</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="studentName" className="block mb-2">
                Select a Teacher/ Student
              </label>
              <select 
                id="studentName" 
                className="border p-2 rounded w-full"
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                disabled={!selectedSectionId || loading}
              >
                <option value="" disabled>
                  Choose a Teacher/ Student
                </option>
                {filteredMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.fullName} ({member.role || 'Student'})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="dateRange" className="block mb-2">
                Date Range
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="border p-2 rounded flex-1"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={loading}
                />
                <span className="flex items-center">to</span>
                <input
                  type="date"
                  className="border p-2 rounded flex-1"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="bg-gray-400 px-4 py-2 rounded text-white"
            >
              Close
            </button>
            <button
              onClick={handleViewIndividualReport}
              className="bg-[#241763] px-4 py-2 rounded text-white"
              disabled={!selectedStudentId || !startDate || !endDate || loading}
            >
              View Individual Report
            </button>
          </div>
        </div>
      </div>

      {viewIndividualReport ? (
        <IndividualAttendanceReport
          isOpen={viewReportOpen}
          onClose={() => setViewReportOpen(false)}
          selectedPerson={allAvailableMembers.find(member => member.id === selectedStudentId)}
          sectionName={selectedSection.sectionName}
          className={selectedClass.className}
          campusName={selectedCampus.name}
          startDate={startDate}
          endDate={endDate}
          userType={selectedUserType}
        />
      ) : viewSectionReport ? (
        <AttendanceReport
          isOpen={viewReportOpen}
          onClose={() => setViewReportOpen(false)}
          sectionId={selectedSectionId}
          sectionName={selectedSection.sectionName}
          className={selectedClass.className}
          campusName={selectedCampus.name}
          isSectionReport={viewSectionReport}
          startDate={startDate}
          endDate={endDate}
          userType={selectedUserType}
        />
      ) : (
        <ViewReportModal
          isOpen={viewReportOpen}
          onClose={() => setViewReportOpen(false)}
          studentId={selectedStudentId}
          sectionId={selectedSectionId}
          isSectionReport={viewSectionReport}
          sectionName={selectedSection.sectionName}
          className={selectedClass.className}
          campusName={selectedCampus.name}
          startDate={startDate}
          endDate={endDate}
          userType={selectedUserType}
        />
      )}
    </>
  );
};

export default Attendance;